// Apolo IDE — Hermes RunCard (P1-E1-T08).
//
// One ROOT run as an opaque card (doctrine: content layer is primary and
// opaque; hover is NEUTRAL bg shift + 1px slide — no per-accent borders).
//   header   → state chip + task (truncate, title attr) + agent name + elapsed
//   children → fan-out tree: indented rows behind a border-l connector, each
//              with a mini state chip + task + per-child elapsed/tokens;
//              >3 children get an aria-expanded toggle (live cards default
//              expanded, recent ones collapsed)
//   footer   → "N/M filhos" progress (fan-out) + tokens + Cancelar
// Cancel is a two-step confirm (same idiom as AgentCard's delete) and only
// exists while the run is non-terminal. Clicking the card body (or the task)
// opens the run detail via onOpen(run.id) — T09 wires the destination.

import { useEffect, useRef, useState } from 'react';
import { Ban, ChevronDown, ChevronRight } from 'lucide-react';
import { doneCount, fmtTokens, isTerminalRunState, runElapsedLabel, runState, runOutcomeMeta } from './runFormat.js';

/** Status chip: icon + color + LABEL, always (never color-only). When a full
 *  `run` is given, the chip is HONEST (done+acted=0 → "sem ações"; runner_lost →
 *  "runner caiu"); otherwise it falls back to the bare state string. */
export function RunStateChip({ state, run, mini = false }) {
  const meta = run ? runOutcomeMeta(run) : runState(state);
  const { Icon } = meta;
  return (
    <span
      className={
        'inline-flex shrink-0 items-center gap-1 rounded-full border border-apolo-border bg-apolo-surface1 ' +
        (mini ? 'px-1.5 py-px text-[10px] ' : 'px-2 py-0.5 text-[11px] ') +
        meta.tone
      }
    >
      <Icon
        size={mini ? 10 : 12}
        strokeWidth={1.5}
        aria-hidden="true"
        className={meta.pulse ? 'animate-pulse motion-reduce:animate-none' : ''}
      />
      {meta.label}
    </span>
  );
}

export default function RunCard({
  run,
  childRuns = [],
  progress = null,
  agentName,
  variant = 'live',
  onCancel,
  onOpen,
  highlight = false,
}) {
  const terminal = isTerminalRunState(run?.state);
  const compact = variant === 'recent';
  const [expanded, setExpanded] = useState(variant !== 'recent');
  const [confirming, setConfirming] = useState(false);
  const rootRef = useRef(null);

  // highlightRunId (navigate event) → scroll the card into view. Ring is a
  // class below; smooth only when motion is allowed.
  useEffect(() => {
    if (!highlight) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    rootRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, [highlight]);

  if (!run) return null;

  const children = Array.isArray(childRuns) ? childRuns : [];
  const showToggle = children.length > 3;
  const showChildren = children.length > 0 && (!showToggle || expanded);
  const elapsed = runElapsedLabel(run);
  const tokens = typeof run.tokens === 'number' && run.tokens > 0 ? run.tokens : 0;
  const childrenDone = doneCount(progress);
  const hasFooter = (progress && progress.total > 0) || tokens > 0 || (!terminal && onCancel);

  function handleCardClick(e) {
    // buttons inside (cancel/toggle/task) own their clicks.
    if (e.target?.closest?.('button')) return;
    onOpen?.(run.id);
  }

  return (
    <article
      ref={rootRef}
      data-testid="hermes-run-card"
      data-run-id={run.id}
      data-variant={variant}
      onClick={handleCardClick}
      className={
        'flex cursor-pointer flex-col gap-2 rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 ' +
        (compact ? 'p-2.5 ' : 'p-3 ') +
        'transition duration-150 ease-out hover:-translate-y-px hover:bg-apolo-surface3 ' +
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0 ' +
        (highlight ? 'ring-2 ring-apolo-accentRing' : '')
      }
    >
      <header className="flex min-w-0 items-center gap-2">
        <RunStateChip state={run.state} run={run} />
        <button
          type="button"
          onClick={() => onOpen?.(run.id)}
          title={run.task || undefined}
          className={
            'min-w-0 flex-1 truncate rounded text-left font-medium text-apolo-textPrimary ' +
            (compact ? 'text-xs ' : 'text-sm ') +
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing'
          }
        >
          {run.task || '(sem tarefa)'}
        </button>
        <span className="flex shrink-0 items-center gap-2 text-[11px] text-apolo-textTertiary">
          {agentName && (
            <span className="max-w-[120px] truncate" title={agentName}>
              {agentName}
            </span>
          )}
          {elapsed && <span className="font-mono">{elapsed}</span>}
        </span>
      </header>

      {showToggle && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className={
            'inline-flex min-h-touch w-fit items-center gap-1 rounded text-[11px] text-apolo-textTertiary ' +
            'transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 ' +
            'focus-visible:ring-apolo-accentRing sm:min-h-0'
          }
        >
          {expanded ? (
            <ChevronDown size={12} strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <ChevronRight size={12} strokeWidth={1.5} aria-hidden="true" />
          )}
          {children.length} filhos
        </button>
      )}

      {showChildren && (
        <ul
          data-testid="hermes-run-children"
          aria-label="Filhos do fan-out"
          className="ml-1.5 flex list-none flex-col gap-1.5 border-l border-apolo-border pl-3"
        >
          {children.map((child, i) => {
            const childElapsed = runElapsedLabel(child);
            return (
              <li key={child.id || i} className="flex min-w-0 items-center gap-1.5">
                <RunStateChip state={child.state} mini />
                <span
                  className="min-w-0 flex-1 truncate text-xs text-apolo-textSecondary"
                  title={child.task || undefined}
                >
                  {child.task || '(sem tarefa)'}
                </span>
                <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] text-apolo-textTertiary">
                  {childElapsed && <span>{childElapsed}</span>}
                  {typeof child.tokens === 'number' && child.tokens > 0 && (
                    <span>{fmtTokens(child.tokens)} tokens</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {hasFooter && (
        <footer className="flex flex-wrap items-center gap-2">
          {progress && progress.total > 0 && (
            <span className="font-mono text-[10px] text-apolo-textTertiary">
              {childrenDone}/{progress.total} filhos
            </span>
          )}
          {tokens > 0 && (
            <span className="font-mono text-[10px] text-apolo-textTertiary">
              {fmtTokens(tokens)} tokens
            </span>
          )}
          {!terminal && onCancel && (
            <span className="ml-auto" onClick={(e) => e.stopPropagation()}>
              {!confirming ? (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className={
                    'inline-flex min-h-touch items-center gap-1 rounded-md border border-apolo-border ' +
                    'bg-apolo-surface2 px-2 py-1 text-[11px] text-apolo-textSecondary transition ' +
                    'hover:bg-apolo-surface3 hover:text-apolo-textPrimary focus:outline-none ' +
                    'focus-visible:ring-2 focus-visible:ring-apolo-accentRing sm:min-h-0'
                  }
                >
                  <Ban size={12} strokeWidth={1.5} aria-hidden="true" />
                  Cancelar
                </button>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-1.5 rounded border border-apolo-error/40 bg-apolo-errorBg p-1.5">
                  <span className="text-[11px] text-apolo-textSecondary">Cancelar run?</span>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(false);
                      onCancel?.(run.id);
                    }}
                    className="inline-flex min-h-touch items-center rounded bg-apolo-error px-2 py-1 text-[11px] font-medium text-apolo-bg transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing sm:min-h-0"
                  >
                    Confirmar cancelamento
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="inline-flex min-h-touch items-center rounded px-2 py-1 text-[11px] text-apolo-textSecondary transition hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing sm:min-h-0"
                  >
                    Voltar
                  </button>
                </span>
              )}
            </span>
          )}
        </footer>
      )}
    </article>
  );
}
